import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Palette, Pencil, Timer, Cpu, CheckCircle2, Layers, Percent, WifiOff } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ScrollReveal } from '../../components/ScrollReveal';
import { AIDrawingLiveDemo } from '../../components/AIDrawingLiveDemo';

const steps = [
  { icon: Pencil, title: 'Desenhe com o dedo', desc: 'Um traço livre, com paleta de cores e tamanhos de pincel, num canvas dedicado.' },
  { icon: Timer, title: 'Aguarda cerca de 3 segundos', desc: 'Depois da última "tacada" sem novos toques, o desenho é recortado automaticamente.' },
  { icon: Cpu, title: 'Um classificador local analisa', desc: 'Um modelo rodando no próprio aparelho reconhece o traço, sem precisar de internet.' },
  { icon: CheckCircle2, title: 'Só mostra se tiver certeza', desc: 'O resultado só aparece quando a confiança do modelo passa de 80%.' },
];

const techStats = [
  { icon: Layers, value: '62', label: 'classes reconhecidas', desc: 'Dígitos 0–9, letras A–Z e a–z.' },
  { icon: Cpu, value: 'EMNIST + AZ', label: 'bases de treino', desc: 'EMNIST ByClass e AZ Kaggle, os mesmos datasets usados por pesquisa em reconhecimento de caracteres.' },
  { icon: Percent, value: '80%', label: 'confiança mínima', desc: 'Calibrada por temperature scaling (Guo et al., 2017), pra evitar palpites errados com "certeza demais".' },
  { icon: WifiOff, value: '100%', label: 'local', desc: 'TensorFlow Lite rodando no aparelho — funciona sem internet.' },
];

const DesenhoIAPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--warm)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Início
          </Link>

          <div className="inline-flex items-center gap-2 bg-warm/10 dark:bg-warm/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-5">
            <Palette className="w-4 h-4 text-warm-ink dark:text-warm" />
            Desenho com IA
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground max-w-3xl leading-[1.1]">
            Desenho que ganha vida com <span className="text-warm-ink dark:text-warm">IA</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground mt-4 max-w-2xl">
            Reconhecimento de letras e números com inteligência artificial, em tempo real —
            sem precisar apertar nenhum botão.
          </p>
        </div>
      </section>

      {/* Demo ao vivo, centralizada */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-sm mx-auto">
              <AIDrawingLiveDemo />
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Passo a passo */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">
              Como funciona, passo a passo
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {steps.map((step, i) => (
                <div key={step.title} className="relative rounded-2xl bg-warm/[0.06] dark:bg-warm/[0.11] p-6">
                  <span className="absolute top-4 right-4 text-3xl font-bold text-warm/25 dark:text-warm/30 select-none">
                    {i + 1}
                  </span>
                  <span className="w-11 h-11 rounded-xl bg-background flex items-center justify-center mb-4 shadow-sm">
                    <step.icon className="w-5 h-5 text-warm-ink dark:text-warm" />
                  </span>
                  <h3 className="font-semibold text-foreground mb-1.5">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Por trás do modelo */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-2">
              Por trás do modelo
            </h2>
            <p className="text-muted-foreground text-center max-w-xl mx-auto mb-10">
              Nenhum dado inventado — é assim que o classificador do app funciona de verdade.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {techStats.map((stat) => (
                <div key={stat.label} className="rounded-2xl bg-card shadow-sm p-6 text-center">
                  <stat.icon className="w-6 h-6 text-warm-ink dark:text-warm mx-auto mb-3" />
                  <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-sm font-semibold text-foreground mt-0.5">{stat.label}</div>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-2">{stat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA final */}
      <section className="py-16 bg-warm/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Quer ver os outros recursos?
          </h2>
          <Link
            to="/recursos/rotinas"
            className="inline-flex items-center gap-1.5 font-sans font-bold text-[15px] px-7 py-3.5 rounded-full bg-primary text-white hover:bg-primary/90 transition-transform hover:-translate-y-0.5"
          >
            Conhecer Rotinas e Alarmes
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DesenhoIAPage;
